// iTunes Search/Lookup API storefronts (ISO 3166-1 alpha-2 country codes).
// Apple's iTunes Search API allows querying any App Store country with no ToS
// restriction on the set of countries — requests are per-country lookup calls
// made from the client browser, not bulk scraping.
// Expanded from a small launch set into the current broad country coverage.

// Region buckets, in coverage order (most-covered region first). Each storefront
// below carries one of these as its `region`, and the values double as the
// <optgroup> labels in the Chart Browser country picker — so they are plain,
// human-readable strings. See groupStorefrontsByRegion().
export const REGIONS = Object.freeze({
  ENGLISH: 'English-primary',
  WESTERN_EUROPE: 'Western Europe',
  EASTERN_EUROPE: 'Eastern Europe',
  EAST_ASIA: 'East Asia',
  SOUTHEAST_ASIA: 'Southeast Asia',
  SOUTH_ASIA: 'South Asia',
  MENA: 'Middle East & North Africa',
  SUB_SAHARAN_AFRICA: 'Sub-Saharan Africa',
  AMERICAS: 'Americas',
});

export const STOREFRONTS = [
  // English-primary
  { code: 'us', name: 'United States', region: REGIONS.ENGLISH, primaryLang: 'en_us' },
  { code: 'gb', name: 'United Kingdom', region: REGIONS.ENGLISH, primaryLang: 'en_gb' },
  { code: 'ca', name: 'Canada', region: REGIONS.ENGLISH, primaryLang: 'en_ca', secondaryLangs: ['fr'] },
  { code: 'au', name: 'Australia', region: REGIONS.ENGLISH, primaryLang: 'en_au' },
  { code: 'nz', name: 'New Zealand', region: REGIONS.ENGLISH, primaryLang: 'en_nz' },
  { code: 'ie', name: 'Ireland', region: REGIONS.ENGLISH, primaryLang: 'en_ie' },
  // Western Europe
  { code: 'de', name: 'Germany', region: REGIONS.WESTERN_EUROPE, primaryLang: 'de_de' },
  { code: 'fr', name: 'France', region: REGIONS.WESTERN_EUROPE, primaryLang: 'fr_fr' },
  { code: 'it', name: 'Italy', region: REGIONS.WESTERN_EUROPE, primaryLang: 'it_it' },
  { code: 'es', name: 'Spain', region: REGIONS.WESTERN_EUROPE, primaryLang: 'es_es' },
  { code: 'pt', name: 'Portugal', region: REGIONS.WESTERN_EUROPE, primaryLang: 'pt_pt' },
  { code: 'nl', name: 'Netherlands', region: REGIONS.WESTERN_EUROPE, primaryLang: 'nl_nl' },
  { code: 'be', name: 'Belgium', region: REGIONS.WESTERN_EUROPE, primaryLang: 'nl_be', secondaryLangs: ['fr', 'de'] },
  { code: 'at', name: 'Austria', region: REGIONS.WESTERN_EUROPE, primaryLang: 'de_at' },
  { code: 'ch', name: 'Switzerland', region: REGIONS.WESTERN_EUROPE, primaryLang: 'de_ch', secondaryLangs: ['fr', 'it'] },
  { code: 'se', name: 'Sweden', region: REGIONS.WESTERN_EUROPE, primaryLang: 'sv_se' },
  { code: 'no', name: 'Norway', region: REGIONS.WESTERN_EUROPE, primaryLang: 'nb_no' },
  { code: 'dk', name: 'Denmark', region: REGIONS.WESTERN_EUROPE, primaryLang: 'da_dk' },
  { code: 'fi', name: 'Finland', region: REGIONS.WESTERN_EUROPE, primaryLang: 'fi_fi' },
  { code: 'gr', name: 'Greece', region: REGIONS.WESTERN_EUROPE, primaryLang: 'el_gr' },
  // Eastern Europe
  { code: 'pl', name: 'Poland', region: REGIONS.EASTERN_EUROPE, primaryLang: 'pl_pl' },
  { code: 'cz', name: 'Czech Republic', region: REGIONS.EASTERN_EUROPE, primaryLang: 'cs_cz' },
  { code: 'hu', name: 'Hungary', region: REGIONS.EASTERN_EUROPE, primaryLang: 'hu_hu' },
  { code: 'ro', name: 'Romania', region: REGIONS.EASTERN_EUROPE, primaryLang: 'ro_ro' },
  { code: 'ru', name: 'Russia', region: REGIONS.EASTERN_EUROPE, primaryLang: 'ru_ru' },
  { code: 'tr', name: 'Turkey', region: REGIONS.EASTERN_EUROPE, primaryLang: 'tr_tr' },
  { code: 'ua', name: 'Ukraine', region: REGIONS.EASTERN_EUROPE, primaryLang: 'uk_ua' },
  // East Asia
  { code: 'jp', name: 'Japan', region: REGIONS.EAST_ASIA, primaryLang: 'ja_jp' },
  { code: 'kr', name: 'South Korea', region: REGIONS.EAST_ASIA, primaryLang: 'ko_kr' },
  { code: 'cn', name: 'China mainland', region: REGIONS.EAST_ASIA, primaryLang: 'zh_cn' },
  { code: 'hk', name: 'Hong Kong', region: REGIONS.EAST_ASIA, primaryLang: 'zh_hk' },
  { code: 'tw', name: 'Taiwan', region: REGIONS.EAST_ASIA, primaryLang: 'zh_tw' },
  // Southeast Asia
  { code: 'sg', name: 'Singapore', region: REGIONS.SOUTHEAST_ASIA, primaryLang: 'en_sg' },
  { code: 'id', name: 'Indonesia', region: REGIONS.SOUTHEAST_ASIA, primaryLang: 'id_id' },
  { code: 'th', name: 'Thailand', region: REGIONS.SOUTHEAST_ASIA, primaryLang: 'th_th' },
  { code: 'my', name: 'Malaysia', region: REGIONS.SOUTHEAST_ASIA, primaryLang: 'ms_my' },
  { code: 'ph', name: 'Philippines', region: REGIONS.SOUTHEAST_ASIA, primaryLang: 'en_ph' },
  { code: 'vn', name: 'Vietnam', region: REGIONS.SOUTHEAST_ASIA, primaryLang: 'vi_vn' },
  { code: 'kh', name: 'Cambodia', region: REGIONS.SOUTHEAST_ASIA, primaryLang: 'km_kh' },
  // South Asia
  { code: 'in', name: 'India', region: REGIONS.SOUTH_ASIA, primaryLang: 'hi_in' },
  { code: 'pk', name: 'Pakistan', region: REGIONS.SOUTH_ASIA, primaryLang: 'ur_pk' },
  { code: 'lk', name: 'Sri Lanka', region: REGIONS.SOUTH_ASIA, primaryLang: 'si_lk' },
  { code: 'bd', name: 'Bangladesh', region: REGIONS.SOUTH_ASIA, primaryLang: 'bn_bd' },
  // Middle East & North Africa
  { code: 'sa', name: 'Saudi Arabia', region: REGIONS.MENA, primaryLang: 'ar_sa' },
  { code: 'ae', name: 'UAE', region: REGIONS.MENA, primaryLang: 'ar_ae' },
  { code: 'il', name: 'Israel', region: REGIONS.MENA, primaryLang: 'he_il' },
  { code: 'eg', name: 'Egypt', region: REGIONS.MENA, primaryLang: 'ar_eg' },
  { code: 'kw', name: 'Kuwait', region: REGIONS.MENA, primaryLang: 'ar_kw' },
  { code: 'qa', name: 'Qatar', region: REGIONS.MENA, primaryLang: 'ar_qa' },
  { code: 'bh', name: 'Bahrain', region: REGIONS.MENA, primaryLang: 'ar_bh' },
  { code: 'om', name: 'Oman', region: REGIONS.MENA, primaryLang: 'ar_om' },
  { code: 'jo', name: 'Jordan', region: REGIONS.MENA, primaryLang: 'ar_jo' },
  { code: 'ma', name: 'Morocco', region: REGIONS.MENA, primaryLang: 'ar_ma' },
  { code: 'lb', name: 'Lebanon', region: REGIONS.MENA, primaryLang: 'ar_lb' },
  { code: 'tn', name: 'Tunisia', region: REGIONS.MENA, primaryLang: 'ar_tn' },
  { code: 'dz', name: 'Algeria', region: REGIONS.MENA, primaryLang: 'ar_dz' },
  // Sub-Saharan Africa
  { code: 'za', name: 'South Africa', region: REGIONS.SUB_SAHARAN_AFRICA, primaryLang: 'en_za' },
  { code: 'ng', name: 'Nigeria', region: REGIONS.SUB_SAHARAN_AFRICA, primaryLang: 'en_ng' },
  { code: 'ke', name: 'Kenya', region: REGIONS.SUB_SAHARAN_AFRICA, primaryLang: 'sw_ke' },
  { code: 'gh', name: 'Ghana', region: REGIONS.SUB_SAHARAN_AFRICA, primaryLang: 'en_gh' },
  { code: 'tz', name: 'Tanzania', region: REGIONS.SUB_SAHARAN_AFRICA, primaryLang: 'sw_tz' },
  { code: 'ug', name: 'Uganda', region: REGIONS.SUB_SAHARAN_AFRICA, primaryLang: 'en_ug' },
  { code: 'zm', name: 'Zambia', region: REGIONS.SUB_SAHARAN_AFRICA, primaryLang: 'en_zm' },
  { code: 'ci', name: "Cote d'Ivoire", region: REGIONS.SUB_SAHARAN_AFRICA, primaryLang: 'fr_ci' },
  { code: 'sn', name: 'Senegal', region: REGIONS.SUB_SAHARAN_AFRICA, primaryLang: 'fr_sn' },
  // Americas
  { code: 'br', name: 'Brazil', region: REGIONS.AMERICAS, primaryLang: 'pt_br' },
  { code: 'mx', name: 'Mexico', region: REGIONS.AMERICAS, primaryLang: 'es_mx' },
  { code: 'ar', name: 'Argentina', region: REGIONS.AMERICAS, primaryLang: 'es_ar' },
  { code: 'co', name: 'Colombia', region: REGIONS.AMERICAS, primaryLang: 'es_co' },
  { code: 'cl', name: 'Chile', region: REGIONS.AMERICAS, primaryLang: 'es_cl' },
  { code: 'pe', name: 'Peru', region: REGIONS.AMERICAS, primaryLang: 'es_pe' },
  { code: 've', name: 'Venezuela', region: REGIONS.AMERICAS, primaryLang: 'es_ve' },
  { code: 'cr', name: 'Costa Rica', region: REGIONS.AMERICAS, primaryLang: 'es_cr' },
  { code: 'do', name: 'Dominican Republic', region: REGIONS.AMERICAS, primaryLang: 'es_do' },
  { code: 'ec', name: 'Ecuador', region: REGIONS.AMERICAS, primaryLang: 'es_ec' },
  { code: 'gt', name: 'Guatemala', region: REGIONS.AMERICAS, primaryLang: 'es_gt' },
  { code: 'pa', name: 'Panama', region: REGIONS.AMERICAS, primaryLang: 'es_pa' },
  { code: 'uy', name: 'Uruguay', region: REGIONS.AMERICAS, primaryLang: 'es_uy' },
  { code: 'bo', name: 'Bolivia', region: REGIONS.AMERICAS, primaryLang: 'es_bo' },
  { code: 'py', name: 'Paraguay', region: REGIONS.AMERICAS, primaryLang: 'es_py' },
];

const STOREFRONT_BY_CODE = new Map(STOREFRONTS.map((sf) => [sf.code, sf]));

// Bucket label for any storefront missing a `region`. None of the entries above
// lack one (a test enforces that), but the grouping helper still needs a home
// for stray entries so it never silently drops one.
const UNGROUPED_REGION = 'Other';

/**
 * Group storefronts into region buckets for a country picker's <optgroup>s.
 *
 * Pure and non-mutating: it returns fresh bucket arrays and never touches the
 * input or its entries. Buckets appear in the order their region is first seen
 * in `storefronts` (the coverage order encoded in STOREFRONTS); within a bucket
 * the storefronts keep their original input order. Any entry missing a `region`
 * is collected into a trailing "Other" bucket, so no storefront is ever dropped
 * or duplicated.
 *
 * @param {{code: string, name: string, region?: string, primaryLang?: string, secondaryLangs?: string[]}[]} storefronts
 * @returns {{region: string, storefronts: {code: string, name: string, region?: string, primaryLang?: string, secondaryLangs?: string[]}[]}[]}
 */
export function groupStorefrontsByRegion(storefronts) {
  /** @type {Map<string, {code: string, name: string, region?: string, primaryLang?: string, secondaryLangs?: string[]}[]>} */
  const buckets = new Map();
  for (const sf of storefronts ?? []) {
    if (!sf) continue;
    const region = sf.region || UNGROUPED_REGION;
    const bucket = buckets.get(region);
    if (bucket) bucket.push(sf);
    else buckets.set(region, [sf]);
  }
  const groups = [...buckets].map(([region, sfs]) => ({ region, storefronts: sfs }));
  // Keep the catch-all "Other" bucket trailing regardless of where the first
  // region-less entry happened to appear in the input.
  const otherIdx = groups.findIndex((g) => g.region === UNGROUPED_REGION);
  if (otherIdx !== -1) groups.push(groups.splice(otherIdx, 1)[0]);
  return groups;
}

// Map from UI locale to the iTunes storefront used for the initial search query.
// Searching from the locale-matching storefront returns results in the user's
// language (e.g. 「メモ」finds Apple Notes in Japanese on jp, not the US store).
const LOCALE_SEARCH_COUNTRY = {
  en: 'us',
  ja: 'jp',
  'zh-CN': 'cn',
  'zh-Hant': 'tw',
};

/**
 * Return the iTunes country code to use for the initial search given a UI locale.
 * Falls back to 'us' for any unrecognised locale.
 * @param {string} locale
 * @returns {string}
 */
export function searchCountryForLocale(locale) {
  return LOCALE_SEARCH_COUNTRY[locale] ?? 'us';
}

/**
 * Return the iTunes Search API language code for a known storefront country.
 * The value is omitted when the country is not in our storefront metadata, so
 * unknown callers retain Apple's default language behavior.
 * @param {string|null|undefined} country
 * @returns {string|null}
 */
export function searchLangForCountry(country) {
  const code = String(country ?? '').trim().toLowerCase();
  return STOREFRONT_BY_CODE.get(code)?.primaryLang ?? null;
}

/**
 * Return the storefront's primary language as an ISO-639-ish base code matching
 * Apple's languageCodesISO2A values, e.g. "ja_jp" -> "ja".
 * @param {string|null|undefined} country
 * @returns {string|null}
 */
export function primaryLanguageForCountry(country) {
  const code = String(country ?? '').trim().toLowerCase();
  return baseLanguageCode(STOREFRONT_BY_CODE.get(code)?.primaryLang);
}

/**
 * Return local language codes that satisfy the storefront-language check.
 * The first entry is the primary local language; later entries are explicit
 * secondary local languages for multilingual storefronts.
 * @param {string|null|undefined} country
 * @returns {string[]}
 */
export function localizationLanguagesForCountry(country) {
  const code = String(country ?? '').trim().toLowerCase();
  return localizationLanguagesForStorefront(STOREFRONT_BY_CODE.get(code));
}

/**
 * Return local language codes for an arbitrary storefront-shaped object.
 * @param {{primaryLang?: string|null, secondaryLangs?: string[]|null}|null|undefined} storefront
 * @returns {string[]}
 */
export function localizationLanguagesForStorefront(storefront) {
  const langs = [
    baseLanguageCode(storefront?.primaryLang),
    ...((storefront?.secondaryLangs ?? []).map(baseLanguageCode)),
  ].filter(Boolean);
  return [...new Set(langs)];
}

/**
 * Return the default iTunes Search API language code for a UI locale.
 * @param {string|null|undefined} locale
 * @returns {string}
 */
export function searchLangForLocale(locale) {
  return searchLangForCountry(searchCountryForLocale(locale));
}

function baseLanguageCode(value) {
  if (typeof value !== 'string') return null;
  const match = value.trim().toLowerCase().match(/^[a-z]{2}/);
  return match ? match[0] : null;
}
